import axios from '@/axiosConnection'
import toastr from '@/toastr'

export default {
  namespaced: true,
  state: {
    items: [],
    copy: {}
  },
  mutations: {
    prepareCopy(state, copy) {
      state.copy = copy
    },
    emptyCopy(state) {
      state.copy = {}
    }
  },
  getters: {
    getAll: state => state.items,
    getByStatus: state => status => state.items.filter(item => item.status_id == status || item.status.id == status),
    getByStatusSlug: state => status => state.items ? state.items.filter(item => item.status_slug == status || item.status.status_slug == status) : [],
    getByProduct: state => product => state.items.filter(item => item.product.id == product),
    getByInternalCode: state => internal_code => state.items.find(item => item.internal_code == internal_code),
    getItem: state => id => state.items.find(item => item.id == id),
    getCopy: state => state.copy
  },
  actions: {

    /**
     * Charge tous les items via l'API dans le state. Si des données sont déjà présentes dans le state, le résolve() est lancé de suite
     * @param   {boolean} wait  Si wait = true, on attends vraiment que les données soient reçues pour lancer le resolve() (impératif pour charger un formulaire de modification)
     * @returns {promise}       Aucune donnée n'est retournée
     */
    getAll: (context, wait = false) => {
      let params = {
        wait: wait,
        api_url: 'reference',
        api_data_path: 'references',
        namespace: 'reference'
      }
      return context.dispatch('getAll', params, {
        root: true
      })
    },
    /**
     * Charge tous les items d'un (ou plusieurs) statut via l'API dans le state. Si des données sont déjà présentes dans le state, le résolve() est lancé de suite
     * @param   array   status  Status
     * @param   boolean wait    Si wait = true, on attends vraiment que les données soient reçues pour lancer le resolve() (impératif pour charger un formulaire de modification)
     * @returns promise         Aucune donnée n'est retournée
     */
    getByStatusSlug: (context, status, wait = false) => {
      let params = {
        wait: wait,
        api_url: 'reference/by/status',
        api_data_path: 'references',
        namespace: 'reference',
        query: 'status=' + status.join('|')
      }
      return context.dispatch('getAll', params, {
        root: true
      })
    },
    /**
     * Charge tous les items d'une affaire via l'API dans le state. Si des données sont déjà présentes dans le state, le résolve() est lancé de suite
     * @param   array   status  Status
     * @param   boolean wait    Si wait = true, on attends vraiment que les données soient reçues pour lancer le resolve() (impératif pour charger un formulaire de modification)
     * @returns promise         Aucune donnée n'est retournée
     */
    getAllByBusinessNumber: (context, {
      wait = false,
      business,
      secondary_api_data_path
    }) => {
      let params = {
        wait: wait,
        api_url: 'reference/by/business',
        api_data_path: 'references.rented',
        secondary_api_data_path: secondary_api_data_path,
        namespace: 'reference',
        query: 'business=' + business
      }
      return context.dispatch('getAll', params, {
        root: true
      })
    },
    /**
     * Charge tous les items via l'API dans le state avec des données limitées. Si des données sont déjà présentes dans le state, le résolve() est lancé de suite
     * @param   {boolean} wait  Si wait = true, on attends vraiment que les données soient reçues pour lancer le resolve() (impératif pour charger un formulaire de modification)
     * @returns {promise}       Aucune donnée n'est retournée
     */
    getAllForSelect: (context, wait = false) => {
      let params = {
        wait: wait,
        api_url: 'reference/index/simple',
        api_data_path: 'references',
        namespace: 'reference'
      }
      return context.dispatch('getAll', params, {
        root: true
      })
    },
    getItem: (context, {
      id,
      wait = false,
      add = false
    }) => {
      let params = {
        wait: wait,
        api_url: 'reference',
        namespace: 'reference',
        id: id,
        add: add
      }
      return context.dispatch('getItem', params, {
        root: true
      })
    },
    getItemByInternalCode: (context, internal_code) => {
      return new Promise(function (resolve, reject) {
        let data = {
          internal_code: internal_code
        }
        axios.post("reference/getReferenceByInternalCode", data)
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error)
          })
      })
    },
    /**
     * QR-005 : PDF des étiquettes QR.
     * @param {object} payload  Soit {references: [{id, quantity}]}, soit {filters, quantity}
     * @returns {Promise<Blob>}
     */
    printLabels: (context, payload) => {
      return new Promise(function (resolve, reject) {
        axios.post("reference/labels", payload, { responseType: 'blob' })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error)
          })
      })
    },
    /**
     * QR-003 : normalise une valeur scannée (URL de QR code) en code exploitable.
     * Appelé uniquement pour les URL : un code barre n'entraîne aucun appel réseau.
     */
    resolveScan: (context, scanned_value) => {
      return new Promise(function (resolve, reject) {
        axios.post("reference/resolve-scan", { scanned_value: scanned_value })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error)
          })
      })
    },
    /**
     * QR-002 : récupère le QR code (SVG) de la référence.
     * L'API est derrière un firewall Bearer : une <img src> ne serait pas authentifiée,
     * on récupère donc le SVG ici pour l'injecter inline.
     */
    getQrCode: (context, id) => {
      return new Promise(function (resolve, reject) {
        axios.get("reference/" + id + "/qr-code")
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error)
          })
      })
    },
    addItem(context, item) {
      let params = {
        api_url: 'reference',
        namespace: 'reference',
        item: item
      }
      return context.dispatch('addItem', params, {
        root: true
      })
    },
    saveItem(context, item) {
      let params = {
        api_url: 'reference',
        namespace: 'reference',
        item: item
      }
      return context.dispatch('saveItem', params, {
        root: true
      })
    },
    deleteItem(context, id) {
      let params = {
        api_url: 'reference',
        namespace: 'reference',
        id: id
      }
      return context.dispatch('deleteItem', params, {
        root: true
      })
    },
    setToMaintenance(context, id){
      return new Promise(function (resolve, reject) {
        axios.get("reference/setToMaintenance/"+id)
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error)
          })
      })
    },
    setToInStock(context, id){
      return new Promise(function (resolve, reject) {
        axios.get("reference/setToInStock/"+id)
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error)
          })
      })
    },
    /**
     * Vérifie si une référence unique est bien en stock
     * @param {string}    internal_code   Numéro interne de la référence unique
     * @returns promise                   Informations minimales de la référence unique
     */
    isAvailable: (context, internal_code) => {
      return new Promise(function (resolve, reject) {
        let data = {
          internal_code: internal_code
        }
        axios.post("reference/available", data)
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error)
          })
      })
    },
    /**
     * Retourne le nombre de référence en attente de maintenance via l'API.
     * @returns {promise}       Aucune donnée n'est retournée
     */
    getWaitingCalibrationQuantity: (context) => {
      return new Promise(function (resolve, reject) {
        axios.get("reference/nb-waiting/calibration?limit=0")
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            toastr.error("Le chargement de la liste a échoué")
            reject(error)
          })
      })
    },
    /**
     * Retourne tous les items dont la maintenance est trop longue via l'API.
     * @returns {promise}       Aucune donnée n'est retournée
     */
    getInLongMaintenance: (context) => {
      return new Promise(function (resolve, reject) {
        axios.get("reference/long/maintenance?limit=0")
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            toastr.error("Le chargement de la liste a échoué")
            reject(error)
          })
      })
    },
    /**
     * Retourne le nombre de référence en location via l'API.
     * @returns {promise}       Aucune donnée n'est retournée
     */
    getInLocationQuantity: (context) => {
      return new Promise(function (resolve, reject) {
        axios.get("reference/total/in-location?limit=0")
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            toastr.error("Le chargement de la liste a échoué")
            reject(error)
          })
      })
    },
  }
}



// WEBPACK FOOTER //
// ./src/stores/modules/reference.js